import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { getAppConsistentParams } from "../utils/appUtils.js";
import { getEntityImage } from "../utils/assetUtils.js";
import { getPurchaseRewardAmountForProduct, getProductUrl, isServerSideCheckoutEligible } from "../utils/productUtils.js";
import { decodeHTMLCharacters, formatMoney } from "../utils/stringUtils.js";
import { CampaignType } from "../types/Campaign.js";
import { FallbackImage } from "../Components/FallbackImage.js";
import { ConciergeRetailerIconGroup } from "./components/ConciergeRetailerIconGroup.js";
import { ConciergeRewardPillWithText } from "./components/ConciergeRewardPillWithText.js";
import { Button } from "../Components/ui/button.js";
import { cn } from "../lib/utils.js";
import _uniq from "lodash/uniq";
import { CheckCircleIcon, ShoppingBag } from "lucide-react";
import { themeColor, customColor } from "@brandclub/bc-widgets";
const defaultCardStyles = {
  cardBorderColor: customColor("#dcdddc"),
  cardBorderRadius: 12,
  cardBgColor: themeColor("transparent"),
  cardBgColorHover: customColor("#F6F8FA"),
  cardPadding: 16,
  cardPaddingMobile: 10,
  titleColor: themeColor("primary"),
  titleFontFamily: "inherit",
  titleFontSize: 14,
  titleFontWeight: 500,
  titleLineClamp: 3,
  titleLineHeight: 1.5,
  titleMarginTop: 20,
  priceColor: themeColor("primary"),
  priceFontFamily: "inherit",
  priceFontSize: 20,
  priceFontSizeMobile: 16,
  priceFontWeight: 600,
  retailerIconSize: 25,
  retailerIconSizeMobile: 15,
  imageMaxWidth: 190
};
const defaultResolveColor = (color) => {
  if (typeof color === "string") return color;
  if (color && typeof color === "object" && "value" in color) return color.value;
  return "#000000";
};
const ConciergeProductCard = ({
  product,
  onShareClick,
  onAddToCart,
  onOpenCart,
  isInCart: isInCartFn,
  showCartActions = true,
  cardStyles: externalCardStyles,
  themeFonts = {},
  resolveColor = defaultResolveColor,
  onProductClick
}) => {
  const {
    affiliateRewardAmount,
    hasActiveInstantReward,
    matchedProducts,
    checkoutEligibility,
    retailerId,
    retailPrice,
    stacklineSku,
    title,
    reviewsRating,
    reviewsAverageRating,
    reviewsCount,
    dtcProductPageUrl
  } = product;
  const ratingValue = reviewsRating != null && reviewsRating > 0 ? Math.round(reviewsRating * 10) / 10 : reviewsAverageRating != null && reviewsAverageRating > 0 ? Math.round(reviewsAverageRating * 10) / 10 : null;
  const styles = externalCardStyles ? { ...defaultCardStyles, ...externalCardStyles } : defaultCardStyles;
  const img = getEntityImage(stacklineSku, "product");
  const cleanProductTitle = decodeHTMLCharacters(title || "");
  const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
  const rewardPercent = !!retailPrice && !!purchaseRewardAmount ? purchaseRewardAmount * 100 / retailPrice : 0;
  const matchedProductsRetailerIds = (matchedProducts ?? []).map((matchedProduct) => matchedProduct.retailerId);
  const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams, dtcProductPageUrl });
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 500;
  });
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 500);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const baseFontSize = isMobileView ? 11 : 14;
  const retailerLogoWidth = isMobileView ? styles.retailerIconSizeMobile : styles.retailerIconSize;
  const cardPadding = isMobileView ? styles.cardPaddingMobile : styles.cardPadding;
  const priceFontSize = isMobileView ? styles.priceFontSizeMobile : styles.priceFontSize;
  const isInCart = isInCartFn ? isInCartFn(stacklineSku) : false;
  const canShowCartActions = showCartActions && isServerSideCheckoutEligible(checkoutEligibility) && (onAddToCart || onOpenCart);
  const handleAddToBag = async () => {
    if (onAddToCart) {
      try {
        await onAddToCart(product);
        onOpenCart == null ? void 0 : onOpenCart();
      } catch (error) {
        console.error("Error adding to bag:", error);
      }
    }
  };
  const titleFontKey = styles.titleFontFamily;
  const titleFontData = titleFontKey && titleFontKey !== "inherit" ? themeFonts[titleFontKey] : null;
  const titleFontFamily = titleFontData ? `"${titleFontData.name}", sans-serif` : "inherit";
  const priceFontKey = styles.priceFontFamily;
  const priceFontData = priceFontKey && priceFontKey !== "inherit" ? themeFonts[priceFontKey] : null;
  const priceFontFamily = priceFontData ? `"${priceFontData.name}", sans-serif` : "inherit";
  const fontFaces = [];
  if (titleFontData && titleFontData.format !== "google" && titleFontData.format !== "system") {
    fontFaces.push(`
      @font-face {
        font-family: "${titleFontData.name}";
        src: url("${titleFontData.url}") format("${titleFontData.format}");
        font-display: swap;
      }
    `);
  }
  if (priceFontData && priceFontData.name !== (titleFontData == null ? void 0 : titleFontData.name) && priceFontData.format !== "google" && priceFontData.format !== "system") {
    fontFaces.push(`
      @font-face {
        font-family: "${priceFontData.name}";
        src: url("${priceFontData.url}") format("${priceFontData.format}");
        font-display: swap;
      }
    `);
  }
  const fontFaceCss = fontFaces.join("\n");
  const cardStyle = {
    border: `1px solid ${resolveColor(styles.cardBorderColor)}`,
    width: "100%",
    height: "100%",
    borderRadius: styles.cardBorderRadius,
    padding: cardPadding,
    boxSizing: "border-box",
    transition: "background-color 0.4s ease-in-out"
  };
  const titleStyle = {
    textDecoration: "none",
    color: resolveColor(styles.titleColor),
    ...titleFontFamily !== "inherit" && { fontFamily: titleFontFamily },
    fontSize: styles.titleFontSize,
    fontWeight: styles.titleFontWeight,
    marginTop: styles.titleMarginTop,
    width: "100%",
    overflow: "hidden",
    letterSpacing: 0.36,
    WebkitLineClamp: styles.titleLineClamp,
    lineHeight: styles.titleLineHeight,
    height: `${styles.titleLineClamp * styles.titleLineHeight}em`,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word"
  };
  const priceContainerStyle = {
    color: resolveColor(styles.priceColor),
    ...priceFontFamily !== "inherit" && { fontFamily: priceFontFamily },
    fontSize: priceFontSize,
    fontWeight: styles.priceFontWeight,
    marginTop: 10,
    marginBottom: 10,
    display: "flex",
    columnGap: 12,
    alignItems: "center"
  };
  const imageContainerStyle = {
    width: "calc(100% - 24px)",
    maxWidth: styles.imageMaxWidth,
    aspectRatio: "1/1",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 0"
  };
  const [isHovered, setIsHovered] = useState(false);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        ...cardStyle,
        backgroundColor: isHovered ? resolveColor(styles.cardBgColorHover) : resolveColor(styles.cardBgColor)
      },
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      children: [
        /* @__PURE__ */ jsx("style", { children: `
        ${fontFaceCss}
        .concierge-product-card * {
          mix-blend-mode: multiply;
        }
        .concierge-product-card img[alt*="reward-icon"] {
          mix-blend-mode: normal !important;
        }
      ` }),
        /* @__PURE__ */ jsxs("div", { className: "concierge-product-card", children: [
          /* @__PURE__ */ jsx(
            ConciergeRewardPillWithText,
            {
              onPillClick: onShareClick,
              rewardAmount: affiliateRewardAmount ?? 0,
              rewardCampaignType: CampaignType.AffiliateRewardCampaign,
              containerStyle: { justifyContent: "end" },
              baseFontSize
            }
          ),
          /* @__PURE__ */ jsx("a", { href: targetUrl, target: "_blank", rel: "noreferrer", style: imageContainerStyle, onClick: onProductClick, children: /* @__PURE__ */ jsx(
            FallbackImage,
            {
              src: img,
              fallbackImage: img.replace("media.brandclub.com", "media-api.brandclub.com"),
              alt: title,
              style: {
                maxHeight: "100%",
                maxWidth: "100%",
                aspectRatio: "1",
                objectFit: "contain"
              }
            }
          ) }),
          /* @__PURE__ */ jsx("a", { href: targetUrl, target: "_blank", rel: "noreferrer", style: titleStyle, onClick: onProductClick, children: cleanProductTitle }),
          /* @__PURE__ */ jsxs("div", { style: priceContainerStyle, children: [
            /* @__PURE__ */ jsx("div", { style: { display: "flex", alignItems: "center" }, children: formatMoney(retailPrice) }),
            /* @__PURE__ */ jsx(ConciergeRetailerIconGroup, { retailerIds, iconSize: retailerLogoWidth, maxSize: 5 })
          ] }),
          ratingValue != null && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginTop: 2,
                fontSize: baseFontSize,
                color: resolveColor(styles.titleColor),
                opacity: 0.9
              },
              children: [
                /* @__PURE__ */ jsx("span", { style: { color: "#ED792F" }, children: "★" }),
                /* @__PURE__ */ jsx("span", { children: ratingValue }),
                reviewsCount != null && reviewsCount > 0 && /* @__PURE__ */ jsxs("span", { style: { opacity: 0.85 }, children: [
                  "(",
                  reviewsCount.toLocaleString(),
                  " ratings)"
                ] })
              ]
            }
          ),
          canShowCartActions && /* @__PURE__ */ jsx("div", { style: { marginTop: 8, marginBottom: 8 }, children: isInCart ? /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "outline",
              onClick: onOpenCart,
              className: cn(
                "tw:w-full tw:h-10 tw:text-sm tw:border-[#6b7280] tw:text-[#6b7280]",
                "tw:normal-case tw:hover:border-[#4b5563] tw:hover:bg-[#f9fafb]",
                "max-[500px]:tw:h-9 max-[500px]:tw:text-xs"
              ),
              style: {
                width: "100%",
                height: isMobileView ? 36 : 40,
                fontSize: isMobileView ? 12 : 14,
                borderColor: "#6b7280",
                color: "#6b7280",
                textTransform: "none"
              },
              children: [
                /* @__PURE__ */ jsx(CheckCircleIcon, { style: { width: 16, height: 16, marginRight: 8 } }),
                "Added to Bag"
              ]
            }
          ) : /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "default",
              onClick: handleAddToBag,
              className: cn(
                "tw:w-full tw:h-10 tw:text-sm tw:text-white tw:normal-case",
                "max-[500px]:tw:h-9 max-[500px]:tw:text-xs"
              ),
              style: {
                width: "100%",
                height: isMobileView ? 36 : 40,
                fontSize: isMobileView ? 12 : 14,
                backgroundColor: "var(--color-primary, #1e293b)",
                color: "var(--color-primary-foreground, white)",
                textTransform: "none"
              },
              children: [
                /* @__PURE__ */ jsx(ShoppingBag, { style: { width: 16, height: 16, marginRight: 8 } }),
                "Add to Bag"
              ]
            }
          ) }),
          /* @__PURE__ */ jsx(
            ConciergeRewardPillWithText,
            {
              rewardAmount: purchaseRewardAmount ?? 0,
              rewardCampaignType: hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign,
              rewardPercent,
              rewardPercentText: "reward",
              containerStyle: isMobileView ? { columnGap: 8 } : void 0,
              baseFontSize
            }
          )
        ] })
      ]
    }
  );
};
export {
  ConciergeProductCard
};
//# sourceMappingURL=ConciergeProductCard.js.map
